import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, RefreshCw } from 'lucide-react';
import { itemsAPI } from '../../api';
import { useCurrency } from '../../contexts/CurrencyContext';

const ItemSearchComponent = ({
  placeholder = "Search items...",
  onItemSelect,
  className = "",
  showBarcodeSearch = true,
  showNameSearch = true,
  autoFocus = false,
  disabled = false,
  showOutOfStock = false,
  inputType = "text",
  compact = false,
}) => {
  const { formatPrice } = useCurrency();
  
  // Name search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Barcode search state
  const [barcodeSearch, setBarcodeSearch] = useState('');
  const [isSearchingBarcode, setIsSearchingBarcode] = useState(false);
  const [barcodeSearchResults, setBarcodeSearchResults] = useState([]);
  const [isSearchingBarcodeRealtime, setIsSearchingBarcodeRealtime] = useState(false);
  const [barcodeQueue, setBarcodeQueue] = useState([]);
  const [isProcessingBarcodeQueue, setIsProcessingBarcodeQueue] = useState(false);
  
  // Refs
  const barcodeInputRef = useRef(null);
  const nameInputRef = useRef(null);

  // Auto-focus barcode input on mount if enabled
  useEffect(() => {
    if (autoFocus && barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, [autoFocus]);

  // Name search function
  const handleNameSearch = useCallback(async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      debugger
      const response = await itemsAPI.search(searchTerm);
      setSearchResults(response.data || []);
    } catch (error) {
      console.error('Error searching items:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [searchTerm]);

  // Debounced name search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        handleNameSearch();
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, handleNameSearch]);

  // Barcode search by ID
  const handleBarcodeSearch = useCallback(async () => {
    const trimmed = String(barcodeSearch || '').trim();
    if (!trimmed) return;
    
    // Enqueue the ID and clear input; processing is handled by the queue worker
    setBarcodeQueue(prev => [...prev, trimmed]);
    setBarcodeSearch('');
  }, [barcodeSearch]);

  // Process the barcode queue sequentially
  useEffect(() => {
    const processNext = async () => {
      debugger
      if (isProcessingBarcodeQueue || barcodeQueue.length === 0) return;
      setIsProcessingBarcodeQueue(true);
      setIsSearchingBarcode(true);
      const nextId = barcodeQueue[0];
      try {
        const response = await itemsAPI.getById(nextId, {include_out_of_stock: showOutOfStock});
        if (response && response.data && response.data[0]) {
          onItemSelect(response.data[0]);
        }
      } catch (error) {
        console.error('Error fetching item by ID (queue):', error);
      } finally {
        // dequeue
        setBarcodeQueue(prev => prev.slice(1));
        setIsSearchingBarcode(false);
        setIsProcessingBarcodeQueue(false);
      }
    };
    processNext();
  }, [barcodeQueue, isProcessingBarcodeQueue, onItemSelect]);

  // Realtime typeahead for barcode input
  const handleBarcodeRealtimeSearch = useCallback(async () => {
    const trimmed = String(barcodeSearch || '').trim();
    if (!trimmed) {
      setBarcodeSearchResults([]);
      return;
    }
    setIsSearchingBarcodeRealtime(true);
    try {
      const params = { search: trimmed, limit: 10, include_out_of_stock: showOutOfStock };
      const response = await itemsAPI.getAll(params);
      setBarcodeSearchResults(response?.data || []);
    } catch (error) {
      console.error('Error searching items by barcode:', error);
      setBarcodeSearchResults([]);
    } finally {
      setIsSearchingBarcodeRealtime(false);
    }
  }, [barcodeSearch]);

  // Debounce barcode input search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (String(barcodeSearch || '').trim()) {
        handleBarcodeRealtimeSearch();
      } else {
        setBarcodeSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [barcodeSearch, handleBarcodeRealtimeSearch]);

  // Helper functions for item status
  const getStockStatus = (item) => {
    const currentStock = parseFloat(item.Stock) || 0;
    const minQuantity = parseFloat(item.MinimunQuantity) || 0;

    if (currentStock <= 0) return { status: 'out', color: 'bg-red-100 text-red-800', text: 'Out of Stock' };
    if (currentStock <= minQuantity) return { status: 'low', color: 'bg-yellow-100 text-yellow-800', text: 'Low Stock' };
    return { status: 'good', color: 'bg-green-100 text-green-800', text: 'In Stock' };
  };

  const getExpiryStatus = (item) => {
    if (!item.TimeToZero) return null;
    const expiryDate = new Date(item.TimeToZero);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry <= 0) return { status: 'expired', color: 'bg-red-100 text-red-800', text: 'Expired' };
    if (daysUntilExpiry <= 7) return { status: 'expiring', color: 'bg-yellow-100 text-yellow-800', text: `${daysUntilExpiry} days left` };
    return null;
  };

  const handleItemClick = (item) => {
    onItemSelect(item);
    setSearchTerm('');
    setSearchResults([]);
    setBarcodeSearch('');
    setBarcodeSearchResults([]);
  };

  return (
    <div className={`${compact ? 'space-y-2' : 'space-y-4'} ${className}`}>
      {/* Barcode Search */}
      {showBarcodeSearch && (
        <div>
          {!compact && (
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search by Item Barcode
            </label>
          )}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              ref={barcodeInputRef}
              type={inputType}
              placeholder={placeholder || "Enter Item ID or Barcode..."}
              value={barcodeSearch}
              onChange={(e) => setBarcodeSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleBarcodeSearch();
                }
              }}
              disabled={disabled}
              className={`w-full pl-10 pr-4 ${compact ? 'py-2 text-sm' : 'py-3'} border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed`}
            />
            {(isSearchingBarcode || isSearchingBarcodeRealtime) && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              </div>
            )}
          </div>
          
          {/* Barcode Queue Progress */}
          {(barcodeQueue.length > 0 || isProcessingBarcodeQueue) && (
            <div className="mt-2 text-xs text-gray-600 flex items-center gap-2">
              <div className="h-3 w-3 rounded-full border-b-2 border-blue-600 animate-spin"></div>
              <span>Processing barcode queue{isProcessingBarcodeQueue ? '...' : ''}</span>
              {barcodeQueue.length > 0 && (
                <span className="px-2 py-0.5 bg-gray-100 rounded-full border text-gray-700">{barcodeQueue.length} pending</span>
              )}
            </div>
          )}
          
          {/* Barcode Typeahead Dropdown */}
          {barcodeSearchResults.length > 0 && (
            <div className="relative" style={{ height: 0 }}>
              <div className="absolute left-0 right-0 top-0 mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg bg-white shadow-lg z-[9999]">
                {barcodeSearchResults.map((item) => (
                  <div
                    key={item.ID}
                    onClick={() => handleItemClick(item)}
                    className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 line-clamp-2 break-words">{item.Name}</div>
                        <div className="text-xs text-gray-500 truncate">ID: {item.ID}{item.Barcode ? ` · Barcode: ${item.Barcode}` : ''}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-blue-600">{formatPrice(item.SalePrice || item.Price)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Name Search */}
      {showNameSearch && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search by Item Name
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              ref={nameInputRef}
              type="text"
              placeholder={placeholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={disabled}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
            />
            {isSearching && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              </div>
            )}
            
            {/* Name Search Dropdown */}
            {searchResults.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-2 max-h-60 overflow-auto border border-gray-200 rounded-lg bg-white shadow-lg z-[9999]">
                {searchResults.map((item) => {
                  const stockStatus = getStockStatus(item);
                  const expiryStatus = getExpiryStatus(item);
                  return (
                    <div
                      key={item.ID}
                      onClick={() => handleItemClick(item)}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                          <span className="text-sm font-semibold text-gray-600">
                            {item.Name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-gray-900 line-clamp-2 break-words">{item.Name}</h4>
                          {item.NameUrdu && (
                            <p className="text-xs text-gray-500 mt-1" dir="rtl">{item.NameUrdu}</p>
                          )}
                          <div className="flex gap-1 mt-1 flex-wrap">
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${stockStatus.color}`}>{stockStatus.text}</span>
                            {expiryStatus && (
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${expiryStatus.color}`}>{expiryStatus.text}</span>
                            )}
                          </div>
                          <div className="mt-1 text-xs text-gray-500">
                            <span>ID: {item.ID}</span>
                            {item.Barcode && <span className="ml-2">Barcode: {item.Barcode}</span>}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-blue-600">{formatPrice(item.SalePrice || item.Price)}</p>
                          <p className="text-xs text-gray-500">Stock: {item.Stock}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemSearchComponent;
